import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Stack, Tab, Tabs, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AuthHeader from './auth/AuthHeader';
import AuthLayout from './auth/AuthLayout';
import LoginForm from './auth/LoginForm';
import ResetPasswordForm from './auth/ResetPasswordForm';
import SignupForm from './auth/SignupForm';
import { initialLoginForm, initialPasswordResetForm, initialSignupForm } from './auth/constants';
import { buildPhoneNumber } from '../lib/phone';
import { AUTH_ROUTES } from '../routes';
import {
  completePasswordReset,
  completeSignup,
  loginAccount,
  requestPasswordResetOtp,
  requestSignupOtp,
} from '../services/authApi';

const SIGNUP_OTP_COOLDOWN_SECONDS = 60;
const PASSWORD_RESET_OTP_SENT_MESSAGE =
  'If an active account exists for this email, an OTP has been sent.';

function getOtpCooldownStorageKey(flowName, phoneNumber) {
  return `${flowName}-otp-cooldown:${phoneNumber}`;
}

function readOtpCooldownUntil(flowName, phoneNumber) {
  if (!phoneNumber) {
    return 0;
  }

  const storedValue = window.localStorage.getItem(getOtpCooldownStorageKey(flowName, phoneNumber));
  const parsedValue = Number.parseInt(storedValue ?? '0', 10);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function Login({ mode = 'login', onAuthenticated }) {
  const navigate = useNavigate();
  const [signupForm, setSignupForm] = useState(initialSignupForm);
  const [loginForm, setLoginForm] = useState(initialLoginForm);
  const [passwordResetForm, setPasswordResetForm] = useState(initialPasswordResetForm);
  const [signupOtpRequested, setSignupOtpRequested] = useState(false);
  const [passwordResetOtpRequested, setPasswordResetOtpRequested] = useState(false);
  const [signupOtpCooldownUntil, setSignupOtpCooldownUntil] = useState(0);
  const [passwordResetOtpCooldownUntil, setPasswordResetOtpCooldownUntil] = useState(0);
  const [signupOtpCooldownSecondsLeft, setSignupOtpCooldownSecondsLeft] = useState(0);
  const [passwordResetOtpCooldownSecondsLeft, setPasswordResetOtpCooldownSecondsLeft] = useState(0);
  const [busyAction, setBusyAction] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [authResult, setAuthResult] = useState(null);

  const signupPhoneNumber = useMemo(
    () => buildPhoneNumber(signupForm.countryCode, signupForm.phoneLocalNumber),
    [signupForm.countryCode, signupForm.phoneLocalNumber]
  );

  useEffect(() => {
    const cooldownUntil = readOtpCooldownUntil('signup', signupForm.email);
    setSignupOtpCooldownUntil(cooldownUntil);
  }, [signupForm.email]);

  useEffect(() => {
    const cooldownUntil = readOtpCooldownUntil('password-reset', passwordResetForm.email);
    setPasswordResetOtpCooldownUntil(cooldownUntil);
  }, [passwordResetForm.email]);

  useEffect(() => {
    if (!signupOtpCooldownUntil) {
      setSignupOtpCooldownSecondsLeft(0);
      return undefined;
    }

    function updateCooldownSecondsLeft() {
      const remainingSeconds = Math.max(
        0,
        Math.ceil((signupOtpCooldownUntil - Date.now()) / 1000)
      );
      setSignupOtpCooldownSecondsLeft(remainingSeconds);

      if (remainingSeconds === 0 && signupForm.email) {
        window.localStorage.removeItem(getOtpCooldownStorageKey('signup', signupForm.email));
      }
    }

    updateCooldownSecondsLeft();

    const intervalId = window.setInterval(updateCooldownSecondsLeft, 1000);
    return () => window.clearInterval(intervalId);
  }, [signupForm.email, signupOtpCooldownUntil]);

  useEffect(() => {
    if (!passwordResetOtpCooldownUntil) {
      setPasswordResetOtpCooldownSecondsLeft(0);
      return undefined;
    }

    function updateCooldownSecondsLeft() {
      const remainingSeconds = Math.max(
        0,
        Math.ceil((passwordResetOtpCooldownUntil - Date.now()) / 1000)
      );
      setPasswordResetOtpCooldownSecondsLeft(remainingSeconds);

      if (remainingSeconds === 0 && passwordResetForm.email) {
        window.localStorage.removeItem(
          getOtpCooldownStorageKey('password-reset', passwordResetForm.email)
        );
      }
    }

    updateCooldownSecondsLeft();

    const intervalId = window.setInterval(updateCooldownSecondsLeft, 1000);
    return () => window.clearInterval(intervalId);
  }, [passwordResetForm.email, passwordResetOtpCooldownUntil]);

  function resetStatus() {
    setErrorMessage('');
    setSuccessMessage('');
  }

  function navigateToMode(nextMode) {
    navigate(
      nextMode === 'signup'
        ? AUTH_ROUTES.signup
        : nextMode === 'reset'
          ? AUTH_ROUTES.resetPassword
          : AUTH_ROUTES.login
    );
  }

  function resetFlows(nextMode) {
    setSignupForm(initialSignupForm);
    setLoginForm(initialLoginForm);
    setPasswordResetForm(initialPasswordResetForm);
    setSignupOtpRequested(false);
    setPasswordResetOtpRequested(false);
    setAuthResult(null);
    resetStatus();
  }

  function startSignupOtpCooldown() {
    const cooldownUntil = Date.now() + SIGNUP_OTP_COOLDOWN_SECONDS * 1000;
    setSignupOtpCooldownUntil(cooldownUntil);
    window.localStorage.setItem(
      getOtpCooldownStorageKey('signup', signupForm.email),
      String(cooldownUntil)
    );
  }

  function startPasswordResetOtpCooldown() {
    const cooldownUntil = Date.now() + SIGNUP_OTP_COOLDOWN_SECONDS * 1000;
    setPasswordResetOtpCooldownUntil(cooldownUntil);
    window.localStorage.setItem(
      getOtpCooldownStorageKey('password-reset', passwordResetForm.email),
      String(cooldownUntil)
    );
  }

  async function runAction(actionName, work, onSuccess) {
    resetStatus();
    setBusyAction(actionName);

    try {
      const result = await work();
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusyAction('');
    }
  }

  function handleAuthentication(result, message) {
    setAuthResult(result);
    setSuccessMessage(message);
    onAuthenticated(result);
  }

  function renderStatus() {
    if (errorMessage) {
      return <Alert severity="error">{errorMessage}</Alert>;
    }

    if (successMessage) {
      return <Alert severity="success">{successMessage}</Alert>;
    }

    return null;
  }

  return (
    <AuthLayout>
      <AuthHeader mode={mode} />

      <Tabs
        centered
        onChange={(_, value) => {
          resetFlows(value);
          navigateToMode(value);
        }}
        value={mode === 'signup' ? 'signup' : 'login'}
        variant="fullWidth"
      >
        <Tab label="Sign in" value="login" />
        <Tab label="Sign up" value="signup" />
      </Tabs>

      {renderStatus()}

      {authResult ? (
        <Stack spacing={2.5}>
          <Alert severity="success">
            Authenticated as {authResult.phoneNumber}
            {authResult.email ? ` • ${authResult.email}` : ''}
          </Alert>
          <TextField fullWidth label="JWT token" minRows={5} multiline value={authResult.token} />
          <Button color="secondary" onClick={() => resetFlows(mode)} variant="text">
            Reset
          </Button>
        </Stack>
      ) : mode === 'login' ? (
        <LoginForm
          busyAction={busyAction}
          form={loginForm}
          onChange={setLoginForm}
          onForgotPassword={() => {
            setPasswordResetForm({
              ...initialPasswordResetForm,
              email: loginForm.email,
            });
            setPasswordResetOtpRequested(false);
            resetStatus();
            navigateToMode('reset');
          }}
          onModeChange={(nextMode) => {
            resetFlows(nextMode);
            navigateToMode(nextMode);
          }}
          onSubmit={() =>
            runAction(
              'login',
              () =>
                loginAccount({
                  email: loginForm.email,
                  password: loginForm.password,
                }),
              (result) => handleAuthentication(result, 'Login complete.')
            )
          }
        />
      ) : mode === 'reset' ? (
        <ResetPasswordForm
          busyAction={busyAction}
          cooldownSecondsLeft={passwordResetOtpCooldownSecondsLeft}
          form={passwordResetForm}
          otpRequested={passwordResetOtpRequested}
          onChange={setPasswordResetForm}
          onModeChange={(nextMode) => {
            resetFlows(nextMode);
            navigateToMode(nextMode);
          }}
          onRequestOtp={() =>
            runAction(
              'password-reset-request-otp',
              () => requestPasswordResetOtp({ email: passwordResetForm.email }),
              () => {
                startPasswordResetOtpCooldown();
                setPasswordResetOtpRequested(true);
                setSuccessMessage(PASSWORD_RESET_OTP_SENT_MESSAGE);
              }
            )
          }
          onComplete={() =>
            runAction(
              'password-reset-complete',
              () =>
                completePasswordReset({
                  email: passwordResetForm.email,
                  otpCode: passwordResetForm.otpCode,
                  password: passwordResetForm.password,
                }),
              (result) => handleAuthentication(result, 'Password reset complete.')
            )
          }
          onResendOtp={() =>
            runAction(
              'password-reset-request-otp',
              () => requestPasswordResetOtp({ email: passwordResetForm.email }),
              () => {
                startPasswordResetOtpCooldown();
                setSuccessMessage(PASSWORD_RESET_OTP_SENT_MESSAGE);
              }
            )
          }
        />
      ) : (
        <SignupForm
          busyAction={busyAction}
          cooldownSecondsLeft={signupOtpCooldownSecondsLeft}
          form={signupForm}
          otpRequested={signupOtpRequested}
          onChange={setSignupForm}
          onModeChange={(nextMode) => {
            resetFlows(nextMode);
            navigateToMode(nextMode);
          }}
          onRequestOtp={() =>
            runAction(
              'signup-request-otp',
              () => requestSignupOtp({ phoneNumber: signupPhoneNumber, email: signupForm.email }),
              () => {
                startSignupOtpCooldown();
                setSignupOtpRequested(true);
                setSuccessMessage('OTP sent. Complete the rest of the form.');
              }
            )
          }
          onComplete={() =>
            runAction(
              'signup-complete',
              () =>
                completeSignup({
                  phoneNumber: signupPhoneNumber,
                  otpCode: signupForm.otpCode,
                  email: signupForm.email,
                  password: signupForm.password,
                }),
              (result) => handleAuthentication(result, 'Signup complete.')
            )
          }
          onResendOtp={() =>
            runAction(
              'signup-request-otp',
              () => requestSignupOtp({ phoneNumber: signupPhoneNumber, email: signupForm.email }),
              () => {
                startSignupOtpCooldown();
                setSuccessMessage('A new OTP was sent.');
              }
            )
          }
        />
      )}
    </AuthLayout>
  );
}

export default Login;
